import * as yup from 'yup';

/**
 * Normalizes an email by trimming whitespace and converting to lowercase
 *
 * @param email - Email to normalize
 * @returns Normalized email string
 */
export const normalizeEmail = (email: string): string => {
    return email.trim().toLowerCase();
};

const emailSchema = yup.string().email('Invalid email format').required('Email cannot be empty');

/**
 * Checks if an email already exists in the recipients list
 *
 * @param email - Email to check
 * @param recipients - Array of existing recipients
 * @returns boolean indicating if email is already present
 */
export const isEmailDuplicate = (email: string, recipients: string[]): boolean => {
    return recipients?.includes(email) ?? false;
};

/**
 * Validates and processes an email for addition
 *
 * @param email - Email to validate
 * @param existingRecipients - Array of existing recipients
 * @returns Object with validation status and error message if invalid
 */
export const validateEmailForAddition = async (
    email: string,
    existingRecipients: string[]
): Promise<{ isValid: boolean; error?: string }> => {
    const normalizedEmail = normalizeEmail(email);

    try {
        await emailSchema.validate(normalizedEmail);
    } catch (error) {
        if (error instanceof yup.ValidationError) {
            return { isValid: false, error: error.message };
        }
        return { isValid: false, error: 'Validation error' };
    }

    if (isEmailDuplicate(normalizedEmail, existingRecipients)) {
        return {
            isValid: false,
            error: `Email ${normalizedEmail} is already present`,
        };
    }

    return { isValid: true };
};
